import { provide } from "vue";
import { Flash } from "~/jose_modern/flash";
import { SupplementApi } from "../../stores/supplement-api";
import { useSafeTimeout } from "../utils/timer-utils";

let saveInFlight = false;

export function isSaveInFlight() {
  return saveInFlight;
}

export function isLabelModalOpen() {
  return !!(
    document.querySelector("#logo-modal.is-active") ||
    document.querySelector("#barcode-modal.is-active")
  );
}

// Note: we track in-flight writes for logos but not barcodes, because
// BarcodesController writes to the Barcode model -- not SupplementLabel --
// so barcode requests can't cause lock_version change on the label row.
export function isLabelWriteInFlight() {
  return (window.__logoWritesInFlight || 0) > 0;
}

export function useAutoSave(store, supplementId, delay = 800) {
  const saveTimer = useSafeTimeout();
  let saveAgainWhenDone = false;
  let lastSaveTime = 0;
  const MIN_SAVE_INTERVAL = 500;
  let networkRetryCount = 0;
  const MAX_NETWORK_RETRIES = 3;
  let staleConflictRetries = 0;
  const MAX_STALE_CONFLICT_RETRIES = 2;

  const save = async () => {
    store.debugLog("[AutoSave] Triggering save...");

    // Rate limiting to prevent rapid successive saves
    const now = Date.now();
    if (now - lastSaveTime < MIN_SAVE_INTERVAL) {
      store.debugLog("[AutoSave] Rate limited, deferring save");
      saveTimer.set(save, MIN_SAVE_INTERVAL - (now - lastSaveTime));
      return;
    }

    // Queue save if already saving
    if (saveInFlight) {
      store.debugLog("[AutoSave] Already saving, queuing next save");
      saveAgainWhenDone = true;
      return;
    }

    // Skip save if we're in a conflict state - user must reload first
    if (store.isConflicted) {
      store.debugLog("[AutoSave] Save skipped: isConflicted=true");
      return;
    }

    // Skip save if out of sync - data would be lost; user is already shown a warning banner
    if (store.isOutOfSync) {
      store.debugLog("[AutoSave] Save skipped: isOutOfSync=true");
      return;
    }

    // Defer save while a logo/barcode modal is open or a modal write is
    // still in flight to avoid concurrent writes to the same supplement_label row
    if (isLabelModalOpen() || isLabelWriteInFlight()) {
      store.debugLog("[AutoSave] Deferred: label modal open or write in flight");
      saveTimer.set(save, 1000);
      return;
    }

    saveInFlight = true;
    lastSaveTime = Date.now();
    store.setSaving(true);

    try {
      const payload = store.getPayload();
      store.debugLog("[AutoSave] Sending payload:", payload);
      const { response, data } = await SupplementApi.saveLabel(supplementId, payload);

      if (response.ok) {
        if (data?.lock_version !== undefined) {
          store.updateLockVersion(data.lock_version);
        }

        if (data?.calculated_values !== undefined) {
          store.setCalculatedValues(data.calculated_values);
        }

        if (data?.validation_warnings !== undefined) {
          store.setValidationWarnings(data.validation_warnings);
        }

        store.setSaved();
        store.clearFailedPayload();
        networkRetryCount = 0;
        staleConflictRetries = 0;
        Flash.success("Changes saved");
      } else if (response.status === 409) {
        const serverVersion = data?.lock_version;

        if (
          Number.isFinite(serverVersion) &&
          serverVersion <= store.lockVersion &&
          staleConflictRetries < MAX_STALE_CONFLICT_RETRIES
        ) {
          // Stale conflict: a concurrent response (e.g., logo) already updated
          // our version past this point. Retry with the current version.
          store.debugLog(
            "[AutoSave] Stale 409 (server=%d, local=%d), retrying",
            serverVersion,
            store.lockVersion
          );
          staleConflictRetries++;
          store.setSaving(false);
          saveAgainWhenDone = true;
        } else {
          staleConflictRetries = 0;
          store.setConflicted(true);
          if (serverVersion !== undefined) {
            store.setServerLockVersion(serverVersion);
          }
          store.setError("conflict");
          Flash.error(
            "This label was modified in another session. Please reload to see the latest changes."
          );
        }
      } else {
        staleConflictRetries = 0;
        const errorMsg = Array.isArray(data?.errors)
          ? data.errors.join(", ")
          : data?.errors || "Save failed";
        store.setError(errorMsg);
        store.setFailedPayload(payload);
        Flash.error(errorMsg + " - Click retry to try again");
      }
    } catch (error) {
      console.error("[AutoSave] Save error:", error);
      store.setSaving(false);
      staleConflictRetries = 0;

      if (networkRetryCount < MAX_NETWORK_RETRIES) {
        networkRetryCount++;
        const retryDelay = Math.min(1000 * Math.pow(2, networkRetryCount - 1), 30000);
        store.debugLog(
          `[AutoSave] Network error, retry ${networkRetryCount}/${MAX_NETWORK_RETRIES} in ${retryDelay}ms`
        );
        Flash.warning(`Network error - retrying in ${Math.round(retryDelay / 1000)}s...`);
        saveTimer.set(save, retryDelay);
      } else {
        store.setError("Network error");
        store.setFailedPayload(store.getPayload());
        Flash.error("Network error - changes not saved. Click retry to try again.");
        networkRetryCount = 0;
      }
    } finally {
      saveInFlight = false;
      if (saveAgainWhenDone) {
        saveAgainWhenDone = false;
        save();
      }
    }
  };

  const debouncedSave = () => {
    saveTimer.set(save, delay);
  };

  const retry = () => {
    store.setSaving(false);
    store.setError(null);
    save();
  };

  const fetchCalculations = async () => {
    if (store.isConflicted || store.isReloading) return;

    try {
      const { response, data } = await SupplementApi.fetchLabel(supplementId);
      if (response.ok && data?.calculated_values) {
        store.setCalculatedValues(data.calculated_values);
      }
    } catch (error) {
      console.error("[AutoSave] Failed to fetch calculations:", error);
    }
  };

  provide("triggerSave", save);
  provide("triggerDebouncedSave", debouncedSave);

  return { save, debouncedSave, retry, fetchCalculations };
}
